import { RepairTypesService } from './../../../shared/backend/clients/api/repairTypes.service';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItemsService, RetailersService, CompanyResponse, RepairRequest, WarrantiesService, FilterRequestForCombo, SaleResponse } from '@src/app/shared/backend/clients';
import { SessionService } from "@app/shared/services/session.service";
import { firstValueFrom } from 'rxjs';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { Router } from '@angular/router';
import { ComboData } from '@src/app/shared/components/comboData/comboData';

@Component({
    selector: 'app-add-repair',
    templateUrl: './add-repair.component.html',
    styleUrl: './add-repair.component.scss',
    host: {
        class: 'app-add-repair'
    },
    standalone: false
})
export class AddRepairComponent implements OnInit {

  company: CompanyResponse | null = null;

  showCreateSerialNumberModal: boolean = false;
  hasSubmittedSerialNumberSearch: boolean = false;
  isSerialNumberValid: boolean = false;
  allowItemOperationsSoldByAnotherRetailer: boolean = false;

  serialNumberError: string | null = null;
  itemsList: any;
  itemsTotalRecords: any = 0;
  itemsRowsPerPageOption: number = 10;
  item: any;

  currentDate: Date = new Date();

  combosData: { [key: string]: ComboData } = {
    'retailers': new ComboData(async (filter) => await this.getStores(filter)),
    'repairTypes': new ComboData(async (filter) => await firstValueFrom(this.repairTypesService.apiRepairTypesFilterComboPost(filter)))
  }

  public repairItemFormGroup: FormGroup = this.fb.group({
    serialNumber: [null, [Validators.required]],
    itemId: [null],
    repairDate: [this.currentDate],
    repairTypeId: [[]],
    retailerId: [[]],
    comments: ['', [Validators.maxLength(255)]]
  });

  constructor(
    private fb: FormBuilder,
    public session: SessionService,
    private retailersService: RetailersService,
    private repairTypesService: RepairTypesService,
    private warrantiesService: WarrantiesService,
    private toasterService: ToasterService,
    private router: Router,
    private itemsService: ItemsService) { }

  async ngOnInit() {
    const company = await this.session.getCompanyData();
    this.allowItemOperationsSoldByAnotherRetailer = company.allowItemOperationsSoldByAnotherRetailer ?? false;
    this.initCombosData();
  }

  async onLazyLoadComboData(comboType: 'retailers' | 'repairTypes', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  private initCombosData() {
    this.combosData['repairTypes'].getData(null, 1);
  }

  getComboTotalPages(comboType: 'retailers' | 'repairTypes'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  async getStores(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.combosData['retailers'].rowsPerPageOption
    };

    if (search) {
      payload.textFilter = search;
    }

    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });
    
    const sale = this.item.sales?.find((x: SaleResponse) => !x.isReturned);
    if(!this.allowItemOperationsSoldByAnotherRetailer && sale)
    {
       filters.push({
        propertyName: 'id',
        operator: 'eq',
        values: [sale.retailerId.toString()]
      });
    }
    payload.additionalFilters = filters;

    return await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));
  }

  async searchSerialNumber() {
    this.hasSubmittedSerialNumberSearch = false;
    this.isSerialNumberValid = false;

    this.repairItemFormGroup.controls['retailerId'].setValue([]);
    this.combosData['retailers'].reset();

    if (this.repairItemFormGroup.valid) {
      this.hasSubmittedSerialNumberSearch = true;
      await this.validateSerialNumber(this.repairItemFormGroup.controls['serialNumber'].value);
    }
  }

  qrCodeScanned(data: any) {
    this.repairItemFormGroup.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  async validateSerialNumber(serialNumber: string): Promise<boolean> {
    const filters: any[] = [];
    filters.push({
      propertyName: 'serialNumber',
      operator: FilterOperator.EQUALS,
      values: [serialNumber]
    });
    filters.push(
    {
      propertyName: 'Disposition',
      operator: FilterOperator.NOT_IN,
      values: ['STOLEN']
    });

    const filter = {
      "pageNumber": 1,
      "pageSize": 1,
      "filters": filters
    };

    try {
      const itemsPage = await firstValueFrom(this.itemsService.apiItemsFilterPost(filter));
      this.itemsList = itemsPage.results || [];
      if (itemsPage.results) {
        this.item = itemsPage?.results[0];
        this.repairItemFormGroup.patchValue({
          itemId: this.item?.id
        });
      }

      if (this.itemsList.length === 0) {
        this.isSerialNumberValid = false;
        return false;
      }

      this.combosData['retailers'].additionalFilters = [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        },
        {
          propertyName: 'brands.id',
          operator: FilterOperator.IN,
          values: [this.item.product.brandId.toString()]
        }
      ];

      await this.combosData['retailers'].getData(null, 1);

      this.isSerialNumberValid = true;
      return true;
    } catch (error) {
      console.error('Error validating serial number', error);
      this.isSerialNumberValid = false;
      return false;
    }
  }

  async createRepair() {
    if (this.repairItemFormGroup.valid) {

      // 1. Repair
      const payload: RepairRequest = {
        repairTypeId: this.repairItemFormGroup.get('repairTypeId')?.value?.[0],
        comment: this.repairItemFormGroup.get('comments')?.value,
        operationDate: this.repairItemFormGroup.get('repairDate')?.value,
        retailerId: this.repairItemFormGroup.get('retailerId')?.value?.[0],
        itemId: this.item.id
      };

      const response = await firstValueFrom(this.warrantiesService.apiWarrantiesRepairPost(payload));
      this.toasterService.show('success', 'Information', 'Element saved correctly');
      this.redirectTo(`/repair`);

    }
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl('/blank', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri])});
  }
}
